import type { ReactNode } from 'react';

import { Alerts, Communication, General, Media } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Size } from './types';

export const STATES_ALL_OPTION_VALUE = '';

const statesOptions = [
  { value: STATES_ALL_OPTION_VALUE, label: 'All Campaigns', icon: <General.InfoCircle /> },
  { value: 'active', label: 'Active', icon: <General.Zap /> },
  { value: 'paused', label: 'Paused', icon: <Media.PauseCircle /> },
  { value: 'action-required', label: 'Needs Action', icon: <Alerts.Bell01 /> },
  { value: 'responded', label: 'Replied', icon: <Communication.MessageSquare01 /> },
  { value: 'interrupted', label: 'Interrupted', icon: <Alerts.AlertCircle /> },
  { value: 'finished', label: 'Finished', icon: <General.CheckCircle /> },
  { value: 'researching', label: 'Researching', icon: <General.SearchSm /> },
  { value: 'pending-review', label: 'Pending Review', icon: <General.Eye /> },
] as const;

export type StatesFilterSelectProps = {
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function StatesFilterSelect({
  value,
  defaultValue,
  onChange,
  placeholder = 'Status',
  className,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  onClear,
}: StatesFilterSelectProps) {
  const selectedOption = statesOptions.find((o) => o.value === value);
  const icon = selectedOption?.icon ?? <General.InfoCircle />;

  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={icon}
      options={[...statesOptions]}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
